import { ArrowRight, Plus, Search, X } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, type TempoSearchResponse } from '../../api';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError, OpsReasonBar } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { TimeRangePicker } from '../../components/TimeRangePicker';
import { formatDateTime } from '../../lib/format';
import { rangeFromParams, rangeToParams, resolveRange, TRACE_ID_RE, type TimeRange } from '../../lib/opsQuery';
import { formatMs } from '../../lib/tempo';
import { useApi } from '../../lib/useApi';

/** Span attributes the Observability API allows in searches (spec §115). */
const SEARCHABLE_TAGS = ['service.name', 'registry', 'operation', 'workflow.type', 'queue', 'http.route', 'error.type', 'correlation_id', 'request_id'] as const;

type TraceRow = NonNullable<TempoSearchResponse['traces']>[number];

function tagsFromParams(params: URLSearchParams): [string, string][] {
  return [...params.entries()].filter(([k]) => k.startsWith('tag.')).map(([k, v]) => [k.slice(4), v]);
}

/** `/ops/traces`: trace search by approved attributes, plus jump-to-trace-ID. */
export function TracesPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('traces.title'));
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tags = tagsFromParams(params);
  const range = rangeFromParams(params, '1h');
  const [draft, setDraft] = useState<[string, string][]>(tags.length ? tags : [['service.name', '']]);
  const [jump, setJump] = useState('');
  const [nonce, setNonce] = useState(0);
  const id = useId();
  const key = params.toString();

  const [syncedFrom, setSyncedFrom] = useState(key);
  if (key !== syncedFrom) {
    setSyncedFrom(key);
    if (tags.length) setDraft(tags);
  }

  const result = useApi(
    async (signal) => {
      if (tags.length === 0) return null;
      const { start, end } = resolveRange(range);
      return api.searchTraces({ tags: Object.fromEntries(tags), start: start.toISOString(), end: end.toISOString(), limit: 50 }, { signal });
    },
    [key, nonce],
  );

  const apply = (nextTags: [string, string][], nextRange: TimeRange = range) => {
    const p = new URLSearchParams();
    for (const [k, v] of nextTags) if (k && v.trim()) p.set(`tag.${k}`, v.trim());
    rangeToParams(nextRange, p);
    setParams(p);
    setNonce((n) => n + 1);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    apply(draft);
  };

  const traces = result.status === 'success' && result.data ? (result.data.traces ?? []) : [];

  const columns: Column<TraceRow>[] = [
    {
      key: 'trace',
      header: t('traces.col.trace'),
      cell: (tr) => (
        <Link to={`/ops/traces/${encodeURIComponent(tr.traceID)}`} className="font-mono text-xs text-primary hover:underline" title={tr.traceID}>
          {tr.traceID.slice(0, 16)}…
        </Link>
      ),
    },
    {
      key: 'root',
      header: t('traces.col.root'),
      cell: (tr) => (
        <span>
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tr.rootServiceName ?? '—'}</code> {tr.rootTraceName ?? ''}
        </span>
      ),
    },
    {
      key: 'started',
      header: t('traces.col.started'),
      className: 'whitespace-nowrap',
      cell: (tr) => (tr.startTimeUnixNano && /^\d+$/.test(tr.startTimeUnixNano) ? formatDateTime(new Date(Number(BigInt(tr.startTimeUnixNano) / 1_000_000n)).toISOString()) : '—'),
    },
    { key: 'duration', header: t('traces.col.duration'), numeric: true, cell: (tr) => (tr.durationMs !== undefined ? formatMs(tr.durationMs) : '—') },
  ];

  return (
    <>
      <PageHeader title={t('traces.title')} subtitle={t('traces.subtitle')} />
      <div className="space-y-6">
        <OpsReasonBar />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Section title={t('traces.searchTitle')}>
            <form className="space-y-3" onSubmit={submit}>
              {draft.map(([k, v], i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Select value={k} onValueChange={(nk) => setDraft(draft.map((tag, j) => (j === i ? [nk, tag[1]] : tag)))}>
                    <SelectTrigger className="w-full font-mono text-xs sm:w-48" aria-label={t('traces.attribute', { n: i + 1 })}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEARCHABLE_TAGS.map((tag) => (
                        <SelectItem key={tag} value={tag} className="font-mono text-xs">
                          {tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="text"
                    aria-label={t('traces.value', { n: i + 1 })}
                    value={v}
                    onChange={(e) => setDraft(draft.map((tag, j) => (j === i ? [tag[0], e.target.value] : tag)))}
                    className="min-w-0 flex-1 font-mono"
                    spellCheck={false}
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setDraft(draft.filter((_tag, j) => j !== i))} disabled={draft.length === 1} aria-label={t('traces.remove')} title={t('traces.remove')}>
                    <X />
                  </Button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setDraft([...draft, ['operation', '']])}>
                  <Plus /> {t('traces.addAttribute')}
                </Button>
                <Button type="submit" size="sm" disabled={!draft.some(([, v]) => v.trim())}>
                  <Search /> {t('traces.search')}
                </Button>
              </div>
              <TimeRangePicker key={key} value={range} onChange={(r) => apply(tags, r)} />
            </form>
          </Section>
          <Section title={t('traces.jumpTitle')}>
            <form
              className="grid gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                const tid = jump.trim().toLowerCase();
                if (TRACE_ID_RE.test(tid)) navigate(`/ops/traces/${tid}`);
              }}
            >
              <label htmlFor={`${id}-jump`} className="text-xs font-medium text-muted-foreground">
                {t('traces.jump')}
              </label>
              <Input id={`${id}-jump`} type="text" value={jump} onChange={(e) => setJump(e.target.value)} placeholder={t('traces.jumpPlaceholder')} className="font-mono" spellCheck={false} />
              <Button type="submit" variant="outline" size="sm" className="w-fit" disabled={!TRACE_ID_RE.test(jump.trim())}>
                {t('shared.open')} <ArrowRight />
              </Button>
            </form>
          </Section>
        </div>
        <Section flush>
          {tags.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('traces.emptyTitle')}>{t('traces.emptyBody')}</EmptyState>
            </div>
          ) : null}
          {tags.length > 0 && result.status === 'loading' ? (
            <div className="px-5">
              <Loading label={t('traces.searching')} />
            </div>
          ) : null}
          {tags.length > 0 && result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={() => setNonce((n) => n + 1)} title={t('traces.failed')} />
            </div>
          ) : null}
          {result.status === 'success' && result.data ? (
            <DataTable columns={columns} rows={traces} rowKey={(tr) => tr.traceID} onRowClick={(tr) => navigate(`/ops/traces/${encodeURIComponent(tr.traceID)}`)} empty={<EmptyState title={t('traces.noTraces')} />} />
          ) : null}
        </Section>
      </div>
    </>
  );
}
